import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityId, EntityState } from '@reduxjs/toolkit';
import { Dictionary } from '@reduxjs/toolkit/src/entities/models';
import { useRouteMatch } from 'react-router-dom';
import { MAX_ALLOWED_API_LIMIT } from '../../../utils/constants';
import LoadingButton from '../LoadingButton';
import styles from './styles.module.scss';
import { ButtonVariant } from '@clover/web-lib-core/dist/components/CoreButton/CoreButton';
import { TableCell, TableRow } from '@material-ui/core';

const MAX_ITEMS = MAX_ALLOWED_API_LIMIT;
const MAX_ENTRIES_PER_PAGE = 15;
const FETCH_ALL_RESULTS_AT_PAGE_NUM = 2;

export interface LoadMoreListConfig {
  fetchAllRecordsAtPage?: number;
  maxEntriesPerPage?: number;
  maxLimit?: number;
}

interface ComponentProps<T> {
  config?: LoadMoreListConfig;
  items: Maybe<EntityState<T>>;
  isLoading: boolean;
  isFetching: boolean;
  setApiLimit: (limit: number) => void | React.Dispatch<React.SetStateAction<number>>;
  renderItem: (item: Maybe<T>, index: number) => React.ReactNode;
  renderInitialLoader?: (index: number) => React.ReactNode;
  renderButtonAsTableRow?: boolean;
  loaderLength?: number;
}

const getLoaderLength = (appsLength: number): number => {
  let result = MAX_ENTRIES_PER_PAGE;
  if (appsLength < result && appsLength > 0) result = appsLength;
  return result;
};

export const ListWithLoadMore = <T, >(
  {
    config = {},
    isLoading,
    isFetching,
    items = { ids: [], entities: {} },
    setApiLimit,
    renderItem,
    renderInitialLoader,
    renderButtonAsTableRow,
    loaderLength,
  }: ComponentProps<T>): Nullable<JSX.Element> => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [onNewPage, setOnNewPage] = useState<boolean>();
  const { url } = useRouteMatch();
  const itemIds: EntityId[] = items.ids;
  const itemEntities: Dictionary<T> = items.entities;
  const {
    fetchAllRecordsAtPage = FETCH_ALL_RESULTS_AT_PAGE_NUM,
    maxEntriesPerPage = MAX_ENTRIES_PER_PAGE,
    maxLimit = MAX_ITEMS,
  } = config;
  const currentCapacity = maxEntriesPerPage * currentPage;
  const maxNumberOfPages = Math.ceil(maxLimit / maxEntriesPerPage);
  const isFullyLoading = onNewPage || isLoading;
  const isPartialLoading = !isFullyLoading && isFetching;

  useEffect(() => {
    if (!isFetching && onNewPage) setOnNewPage(false);
  }, [isFetching, onNewPage]);

  useEffect(() => {
    setOnNewPage(true);
    setCurrentPage(1);
  }, [url]);

  const getLimit = useCallback(
    (pageNumber: number) => {
      if (pageNumber >= fetchAllRecordsAtPage) return (maxLimit > MAX_ITEMS ? MAX_ITEMS : maxLimit);
      return (maxEntriesPerPage + 1) * pageNumber;
    },
    [fetchAllRecordsAtPage, maxEntriesPerPage, maxLimit],
  );

  const handleLoadMoreClick = useCallback(
    () => {
      let newPageNumber = currentPage + 1,
        limit = getLimit(newPageNumber);

      setCurrentPage(newPageNumber);
      setApiLimit(limit);
    },
    [currentPage, getLimit, setApiLimit],
  );

  const showLoadMoreButton = (): boolean => {
    return Boolean(isPartialLoading || currentCapacity < itemIds.length && maxNumberOfPages > currentPage);
  };

  const getSliceIndex = (): number => {
    if (currentPage > 1 && isPartialLoading) return maxEntriesPerPage * (currentPage - 1); // stay with previous page capacity until loading is done
    return currentCapacity;
  };

  const button = useMemo(() => {
    return (
      <div className={styles.loadMoreButton}>
        <LoadingButton
          isLoading={isPartialLoading}
          variant={ButtonVariant.PRIMARY}
          text={isPartialLoading ? t('buttons.loading') : t('buttons.load_more')}
          isDisabled={isPartialLoading} onClick={handleLoadMoreClick} />
      </div>
    );
  }, [isPartialLoading, t, handleLoadMoreClick]);

  const slicedIds: EntityId[] = itemIds.slice(0, getSliceIndex());

  if (!itemIds.length && !isLoading) return null;

  return (
    <>
      {
        isFullyLoading && renderInitialLoader ?
          <>
            {
              Array.from(Array(loaderLength || getLoaderLength(slicedIds.length)).keys()).map((item: number, index) => {
                return renderInitialLoader(index);
              })
            }
          </>
          :
          <>
            {
              slicedIds.map((itemId, index) => {
                const item = itemEntities[itemId];
                return renderItem(item, index);
              })
            }
          </>
      }
      {
        showLoadMoreButton() && (
          <>
            {
              renderButtonAsTableRow ?
                <TableRow>
                  <TableCell align={'center'} colSpan={100}>
                    {button}
                  </TableCell>
                </TableRow>
                :
                button
            }
          </>
        )
      }
    </>
  );
};
